import {
  BufferGeometry,
  Clock,
  Color,
  Euler,
  Float32BufferAttribute,
  Scene,
  TextureLoader,
  Vector3,
  PerspectiveCamera,
  WebGLRenderer,
  ShaderMaterial,
  Points
} from 'three'
import watchElementSize from '../../utils/animated/watchElementSize'
import { AnimatedComponentInit, smoothen } from '../../utils/AnimatedValue'
import wave from './shaders/wave'
import watchBcr from '../../utils/animated/watchBcr'
import OrbitControls from '../OrbitControls'
import watchVisibility from '../../utils/animated/watchVisibility'

const DEG = Math.PI / 180
const VIEWING_ANGLE = -17 * DEG

const PLANE_SIZE = 300
const PLANE_SEGMENTS = 800
const PLANE_REPEAT = PLANE_SIZE / PLANE_SEGMENTS

const gradients: {
  [name: string]: {
    img: string
    width: number
  }
} = {
  blue: {
    img:
      'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAIAAAABCAYAAAD0In+KAAAAAXNSR0IArs4c6QAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAAqADAAQAAAABAAAAAQAAAACJcORAAAAAEUlEQVQIHWNUePb4v5kFBwMAEzkDYHD7lsIAAAAASUVORK5CYII=',
    width: 2
  },
  colorful: {
    img:
      'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAABCAYAAADeko4lAAAAAXNSR0IArs4c6QAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAFKADAAQAAAABAAAAAQAAAAA5Fco1AAAANUlEQVQIHWP8EMPwnwEI/v8WZGD4qs7w74s6kNYA0moMDN80GP7/FANJo4AfQN4rIH6NhQYAzewUZT8SgWEAAAAASUVORK5CYII=',
    width: 20
  },
  purple: {
    img:
      'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAIAAAABCAYAAAD0In+KAAAAAXNSR0IArs4c6QAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAAqADAAQAAAABAAAAAQAAAACJcORAAAAAEUlEQVQIHWN8JfH6fw4DAwMAFDcDWlm6N64AAAAASUVORK5CYII=',
    width: 2
  }
}

const pointWave: AnimatedComponentInit<{ el: HTMLElement }> = (
  el,
  args,
  mounted,
  addEventListener
) => {
  const size = watchElementSize(args.interpolate((x) => x.el))
  const visible = watchVisibility(el, mounted)

  const scene = new Scene()
  scene.background = new Color(window.getComputedStyle(el).backgroundColor)
  const camera = new PerspectiveCamera(40, 1, 0.1, 1000)
  camera.rotation.order = 'YXZ'
  camera.rotation.x = VIEWING_ANGLE
  camera.position.x = 0
  camera.position.y = 5
  camera.position.z = 0

  const renderer = new WebGLRenderer({ antialias: true })
  size.addListener(({ width, height }) => {
    camera.aspect = width / height
    camera.updateProjectionMatrix()
    renderer.setSize(width, height)
  })

  // const geometry = new THREE.PlaneGeometry(
  //   PLANE_SIZE,
  //   PLANE_SIZE,
  //   PLANE_SEGMENTS,
  //   PLANE_SEGMENTS
  // )
  const geometry = new BufferGeometry()
  const positions: number[] = []
  const rMax = PLANE_SIZE * PLANE_SIZE
  for (let x = -PLANE_SIZE / 2; x <= PLANE_SIZE / 2; x += PLANE_REPEAT) {
    for (let y = -PLANE_SIZE / 2; y <= PLANE_SIZE / 2; y += PLANE_REPEAT) {
      if (x * x + y * y > rMax) {
        continue
      }
      positions.push(x, y, 0)
    }
  }
  geometry.setAttribute('position', new Float32BufferAttribute(positions, 3))
  geometry.computeBoundingSphere()
  const material = new ShaderMaterial(wave)
  const mesh = new Points(geometry, material)
  mesh.rotation.x = Math.PI / 2
  // mesh.rotation.z = Math.PI / 6
  scene.add(mesh)

  const gradientImg = gradients[el.dataset.colors || ''] || gradients.colorful
  const textureLoader = new TextureLoader()
  textureLoader.load(gradientImg.img, (gradient) => {
    material.uniforms.gradientWidth.value = gradientImg.width
    material.uniforms.gradient.value = gradient
  })

  const clock = new Clock(true)
  clock.start()

  const attachListenersTo = el.closest('section') || renderer.domElement
  const fakeCamera = camera.clone()
  const controls = new OrbitControls(fakeCamera, attachListenersTo)
  controls.enablePan = false
  controls.enableZoom = false
  controls.enableDamping = true
  controls.dampingFactor = 0.9
  controls.rotateSpeed = -0.2
  controls.minPolarAngle = Math.PI / 2 + VIEWING_ANGLE
  controls.maxPolarAngle = Math.PI / 2 + VIEWING_ANGLE
  controls.reset()

  const bcr = watchBcr(el, mounted)
  const scrollPct = smoothen(
    bcr.interpolate((bcr) => {
      if (!bcr) {
        return 0
      }
      return Math.max(0, Math.min(1, -bcr.top / bcr.height))
    })
  )

  const direction = new Vector3(-3, 0, -3)
  addEventListener<HTMLElement>(attachListenersTo)('dblclick', () => {
    camera.rotation.y += Math.PI / 2
  })

  const rotationY = new Euler(0, 0, 0)
  const scrollPositionOffset = new Vector3(0, 0, 0)
  const timePositionOffset = new Vector3(0, 0, 0)
  const virtualPosition = new Vector3(0, 0, 0)
  const animationLoop = () => {
    if (visible.value) {
      // camera.rotation.y += 0.0001
      camera.rotation.y = fakeCamera.rotation.y + 30 * DEG
      rotationY.y = camera.rotation.y

      const elapsedTime = clock.getElapsedTime()
      material.uniforms.uTime.value = elapsedTime

      camera.rotation.x =
        1.5 * VIEWING_ANGLE * (1 - scrollPct.value * 2) - VIEWING_ANGLE / 2
      camera.position.y = 9 * (1 - scrollPct.value * 2) - 5
      scrollPositionOffset
        .set(0, 0, scrollPct.value * 300)
        .applyEuler(rotationY)

      timePositionOffset.set(
        0, //direction.x * elapsedTime,
        0,
        0 //direction.z * elapsedTime
      )

      virtualPosition.copy(scrollPositionOffset).add(timePositionOffset)

      mesh.position.x = virtualPosition.x % PLANE_REPEAT
      mesh.position.z = virtualPosition.z % PLANE_REPEAT
      material.uniforms.positionOffset.value.set(
        -virtualPosition.x + mesh.position.x,
        -virtualPosition.z + mesh.position.z,
        0
      )

      renderer.render(scene, camera)
    }
    requestAnimationFrame(animationLoop)
  }
  el.appendChild(renderer.domElement)
  animationLoop()
}

export default pointWave
